import { ref, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { ATTRIBUTE_CATEGORIES } from 'dashboard/routes/dashboard/settings/attributes/constants';
import { customAttrSectionKey } from './eventSectionKeys';

const CATEGORY_ICON_MAP = {
  info: 'i-lucide-info',
  av: 'i-lucide-speaker',
  catering: 'i-lucide-utensils',
  security: 'i-lucide-shield',
  technical: 'i-lucide-wrench',
  crew: 'i-lucide-users',
  other: 'i-lucide-ellipsis',
};

const CATEGORY_I18N_MAP = {
  info: 'INFO',
  av: 'AV',
  catering: 'CATERING',
  security: 'SECURITY',
  technical: 'TECHNICAL',
  crew: 'CREW',
  other: 'OTHER',
};

const PARENT_CATEGORY_ORDER = ATTRIBUTE_CATEGORIES.map(c =>
  c.key.toLowerCase()
);

const SHIJI_EVENT_ATTRIBUTE_KEYS = new Set([
  'shiji_booking_id',
  'shiji_booking_code',
  'shiji_booking_status',
]);

const hasAttributeValue = value => value != null && value !== '';

const isDiscardedAttribute = attr => Boolean(attr?.discarded_at);

export function useEventCustomAttributeSections({
  event,
  attributesByCategory,
  editableCategoryKeys,
  readOnly,
  hiddenSectionKeys,
}) {
  const store = useStore();
  const { t } = useI18n();

  const customAttributeCategories = computed(
    () => store.getters['customAttributeCategories/getRecords'] || []
  );

  const addedBlocks = ref(new Set());

  const hiddenKeys = computed(() => new Set(unref(hiddenSectionKeys) || []));

  const getSubCategory = key => {
    if (!key.startsWith('sub_')) return null;
    const id = Number(key.replace('sub_', ''));
    return customAttributeCategories.value.find(c => c.id === id) || null;
  };

  const getParentOfCategory = category => {
    if (!category.startsWith('sub_')) return null;
    const sub = getSubCategory(category);
    return sub?.parent_category || null;
  };

  const activeCategories = computed(() => {
    const active = new Set();
    const customAttrs = unref(event)?.custom_attributes || {};
    const byCategory = unref(attributesByCategory) || {};

    Object.entries(byCategory).forEach(([category, attrs]) => {
      if (
        attrs.some(
          a =>
            customAttrs[a.attribute_key] != null &&
            customAttrs[a.attribute_key] !== ''
        )
      ) {
        active.add(category);
      }
    });
    return active;
  });

  const categoryIsVisible = category => {
    if (hiddenKeys.value.has(customAttrSectionKey(category))) return false;
    return (
      activeCategories.value.has(category) || addedBlocks.value.has(category)
    );
  };

  const appendSubcategoriesForParent = (visible, byCategory, parent) => {
    Object.keys(byCategory).forEach(subKey => {
      if (!subKey.startsWith('sub_')) return;
      const sub = getSubCategory(subKey);
      if (sub?.parent_category === parent && categoryIsVisible(subKey)) {
        visible.push(subKey);
      }
    });
  };

  const getAttributesForCategory = category => {
    const attrs = unref(attributesByCategory)?.[category] || [];
    const customAttrs = unref(event)?.custom_attributes || {};

    return attrs.filter(attr => {
      const value = customAttrs[attr.attribute_key];
      if (isDiscardedAttribute(attr)) return hasAttributeValue(value);
      return !SHIJI_EVENT_ATTRIBUTE_KEYS.has(attr.attribute_key);
    });
  };

  const infoCategoryIsVisible = byCategory => {
    if (hiddenKeys.value.has(customAttrSectionKey('info'))) return false;
    const infoAttrs = byCategory.info || [];
    if (!infoAttrs.length) return false;
    if (getAttributesForCategory('info').length) return true;
    return categoryIsVisible('info');
  };

  const visibleCategories = computed(() => {
    const visible = [];
    const byCategory = unref(attributesByCategory) || {};

    if (infoCategoryIsVisible(byCategory)) visible.push('info');

    PARENT_CATEGORY_ORDER.forEach(parent => {
      if (parent !== 'info' && categoryIsVisible(parent)) visible.push(parent);
      appendSubcategoriesForParent(visible, byCategory, parent);
    });

    return visible;
  });

  const visibleCustomAttrSectionKeys = computed(() =>
    visibleCategories.value.map(customAttrSectionKey)
  );

  const isCategoryReadOnly = category => {
    if (!unref(readOnly)) return false;
    const editableKeys = unref(editableCategoryKeys) || [];
    if (!editableKeys.length) return true;

    return !editableKeys.includes(category);
  };

  const getCategoryIcon = category => {
    if (category.startsWith('sub_')) {
      const sub = getSubCategory(category);
      return CATEGORY_ICON_MAP[sub?.parent_category] || 'i-lucide-tags';
    }
    return CATEGORY_ICON_MAP[category] || 'i-lucide-tags';
  };

  const getCategoryLabel = category => {
    if (category.startsWith('sub_')) {
      const sub = getSubCategory(category);
      return sub?.name || category;
    }
    return t(
      `ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.${CATEGORY_I18N_MAP[category] || 'OTHER'}`
    );
  };

  const availableCategoriesToAdd = computed(() => {
    const byCategory = unref(attributesByCategory) || {};
    const editableKeys = unref(editableCategoryKeys) || [];
    const isReadOnly = unref(readOnly);

    return Object.keys(byCategory)
      .filter(c => !visibleCategories.value.includes(c))
      .filter(c =>
        (byCategory[c] || []).some(attr => !isDiscardedAttribute(attr))
      )
      .filter(
        c =>
          !isReadOnly ||
          editableKeys.includes(c) ||
          (c.startsWith('sub_') &&
            editableKeys.includes(getParentOfCategory(c)))
      )
      .map(c => ({
        key: customAttrSectionKey(c),
        label: getCategoryLabel(c),
        icon: getCategoryIcon(c),
      }));
  });

  const canManageBlocks = computed(() => {
    const isReadOnly = unref(readOnly);
    const editableKeys = unref(editableCategoryKeys) || [];
    return !isReadOnly || editableKeys.length > 0;
  });

  const addBlock = category => {
    addedBlocks.value = new Set([...addedBlocks.value, category]);
  };

  const onBlockRemoved = category => {
    const next = new Set(addedBlocks.value);
    next.delete(category);
    addedBlocks.value = next;
  };

  return {
    visibleCategories,
    visibleCustomAttrSectionKeys,
    availableCategoriesToAdd,
    canManageBlocks,
    isCategoryReadOnly,
    getCategoryLabel,
    getCategoryIcon,
    getAttributesForCategory,
    addBlock,
    onBlockRemoved,
  };
}
